import { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { SaveIcon, DocumentReportIcon, TagIcon } from '@heroicons/react/solid'
import mime from "mime";

import {
    BUTTON_VARIANT_TYPES,
    NO_ACCESS_TYPES,
    PERMISSIONS
} from "../../../../utils/constants";
import { editDocumentTags, getReports, downloadReportByVersion, downloadSubsequentFile } from "../../../../utils/api";

import Alert, { ALERT_TYPE } from "../../../Auth/Alert";
import Button from "../../../SharedComponents/Button";
import DashboardContainer from "../../../SharedComponents/DashboardContainer";
import Input from "../../../Auth/Input";
import AccessControl from "../../../SharedComponents/AccessControl";
import useMountStatus from '../../../../hooks/useMountStatus';
import ManageReportMenu from "./ManageReportMenu";
import Tag from "../../../SharedComponents/Tag";
import Upload from "../../../SharedComponents/Upload";
import Toggle from "../../../SharedComponents/Form/Toggle";
import VersionRow from "./VersionRow";
import SectionPlaceholder from "../../../SharedComponents/Loading/SectionPlaceholder";

function ManageReport() {
    const [alert, setAlert] = useState('');
    const [alertType, setAlertType] = useState(ALERT_TYPE.ERROR);
    const [report, setReport] = useState([]);
    const [reportTags, setReportTags] = useState([]);
    const [tagInput, setTagInput] = useState("");
    const [detailOption, setDetailOption] = useState("Version History");
    const [showSubsequentFiles, setShowSubsequentFiles] = useState([]);
    const [changesMade, setChangesMade] = useState(false);
    const [loading, setLoading] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [currentlyDownloading, setCurrentlyDownloading] = useState(false);

    const unmounted = useMountStatus();
    const location = useLocation();
    const navigate = useNavigate();

    const handleSuccessfulEdit = async (message) => {
        setAlert(message);
        setAlertType(ALERT_TYPE.SUCCESS);
        await fetchData();
    }

    const handleEditTag = async () => {
        setSubmitLoading(true);

        try {
            await editDocumentTags(report.identifier, reportTags);
            await handleSuccessfulEdit("You've successfully edited the tags for " + report.name);
        } catch (err) {
            setAlert(err.message);
            setAlertType(ALERT_TYPE.ERROR);
        }

        setSubmitLoading(false);
    }

    const handleDetailOptionClick = (detailOption) => {
        detailOption && setDetailOption(detailOption);
    };

    const handleDownload = async (documentResponse, fileType) => {
        fileType = documentResponse.fileType ? documentResponse.fileType : fileType;
        const mimeType = mime.getType(fileType);

        if (mimeType === "false") {
            setAlertType(ALERT_TYPE.ERROR);
            setAlert("Sorry there was an error on our side, try again later or contact " +
                "techsupport@psychpress.com.au");
            return;
        }

        const blob = await (await fetch(`data:${mimeType};base64,${documentResponse.content}`)).blob();

        const downloadLink = document.createElement("a");
        downloadLink.href = URL.createObjectURL(blob, { type: mimeType });
        downloadLink.download = documentResponse.fileName.split(".").join("_");
        downloadLink.click();
    }

    const handleDownloadVersion = async (documentIdentifier, versionNumber, fileType) => {
        setCurrentlyDownloading(versionNumber);

        try {
            const documentResponse = await downloadReportByVersion(documentIdentifier, versionNumber);
            handleDownload(documentResponse, fileType);
        } catch (err) {
            setAlert(err.message);
            setAlertType(ALERT_TYPE.ERROR);
        }

        setCurrentlyDownloading(false);
    }

    const handleDownloadSubsequentFile = async (documentIdentifier, subsequentFileIdentifier, fileType) => {
        setCurrentlyDownloading(subsequentFileIdentifier);

        try {
            const documentResponse = await downloadSubsequentFile(documentIdentifier, subsequentFileIdentifier);
            handleDownload(documentResponse, fileType);
        } catch (err) {
            setAlert(err.message);
            setAlertType(ALERT_TYPE.ERROR);
        }

        setCurrentlyDownloading(false);
    }

    const fetchData = useCallback(async () => {
        setLoading(true);

        try {
            const reportsResponse = await getReports();
            const report = reportsResponse
                .filter(report => (report.identifier === location.state.reportIdentifier))[0];
            if (unmounted) return;
            setReport(report);
            setReportTags(report.tags[0] ? report.tags[0].split(",") : []);
        } catch (err) {
            if (unmounted) return;
            setAlert(err.message);
            setAlertType(ALERT_TYPE.ERROR);
        }

        setLoading(false);
    }, [location.state.reportIdentifier, unmounted]);

    const handleTagsKeyPress = (key) => {
        if (key === 'Enter' && tagInput.length > 1) {
            setReportTags(tags =>
                [...new Set([...tags, tagInput.toLowerCase().replace(/\W/g, '')])]);
            setTagInput("");
            setChangesMade(true);
        }
    }

    const deleteTag = (tagToDelete) => {
        setChangesMade(true);
        setReportTags(tags => tags.filter(tag => tag !== tagToDelete));
    }

    const handleOnClickSave = () => {
        if (detailOption === "Tags") {
            handleEditTag();
        }
        setChangesMade(false);
    }

    useEffect(() => {
        if (!location.state) {
            navigate("/reports")
        } else {
            location.state.detailOption && setDetailOption(location.state.detailOption);
            fetchData();
        }
    }, [fetchData, location.state, navigate]);


    const handleShowSubsequentFiles = (versionNumber) => {
        setShowSubsequentFiles(showSubsequentFiles => showSubsequentFiles.includes(versionNumber) ?
            showSubsequentFiles.filter(identifier => identifier !== versionNumber) :
            [...showSubsequentFiles, versionNumber]);
    };

    return (
        <DashboardContainer title={"Manage Report"} titleIcon={<DocumentReportIcon className="block h-5 w-5 mr-2"/>}>
            <Alert
                text={alert}
                type={alertType}
                active={alert}
                close={() => setAlert("")}
            />
            <div className="grid grid-flow-row xl:grid-cols-8 lg:grid-cols-2 gap-6 min-h-0 overflow-x-scroll">
                <ManageReportMenu
                    detailOption={detailOption}
                    handleDetailOptionClick={handleDetailOptionClick}
                    loading={loading}
                />
                <div className={"xl:col-span-6 col-span-2"}>
                    <div className={
                        "border-0 rounded-lg relative flex flex-col w-full bg-white outline-none " +
                        "focus:outline-none shadow-sm"
                    }>
                        <div className="flex items-start justify-between pt-4 pb-2 px-4 rounded-t">
                            <h3 className="text-md font-medium leading-6 text-indigo-900 ">
                                {loading ?
                                    <div
                                        className={"animate-pulse px-4 py-3 h-5 bg-blue-gray-200 rounded-lg h-4 w-48"}
                                    />
                                    :
                                    detailOption === "Version History" ?
                                        <span className="flex">
                                            Version History of report
                                            <div className="ml-1 text-secondary-600">
                                                {(report && report.name)}
                                            </div>
                                        </span> :
                                        detailOption === "Tags" ?
                                            <span className="flex">Edit the tags for
                                                <div className="ml-1 text-secondary-600">
                                                    {(report && report.name)}
                                                </div>
                                            </span> :
                                            detailOption === "Review/Release" ?
                                                <span className="flex">
                                                    Review/Release
                                                    <div className="ml-1 text-secondary-600">
                                                        {(report && report.name)}
                                                    </div>
                                                </span> :
                                                detailOption === "Reupload" ?
                                                    <span className="flex">
                                                        Upload new version of
                                                        <div className="ml-1 text-secondary-600">
                                                            {(report && report.name)}
                                                        </div>
                                                    </span> :
                                                    <>
                                                    </>
                                }
                            </h3>
                        </div>
                        <div className="px-4">
                            {loading ?
                                <div
                                    className={"animate-pulse px-4 py-3 h-5 bg-blue-gray-200 rounded-lg h-4 w-2/4"}/>
                                :
                                <p className="text-sm text-gray-600">
                                    {
                                        detailOption === "Version History" ?
                                            "Find below all report versions" :
                                            detailOption === "Review/Release" ?
                                                "Use this form to choose who you want this report to be reviewed by " +
                                                "and when you want it to be released" :
                                                detailOption === "Reupload" ?
                                                    "Use the interactive upload section below to re-upload a report. " +
                                                    "All previous versions will still be accessible via the " +
                                                    "'Versions' tab" :
                                                    ""
                                    }
                                </p>
                            }
                        </div>

                        <div className="relative px-4 flex-auto mt-4 mb-4">
                            {detailOption === "Version History" &&
                                (loading ?
                                        <SectionPlaceholder/>
                                        :
                                        <>
                                            <div className={"overflow-hidden border-b border-none shadow-sm " +
                                                "sm:rounded-lg"}
                                            >
                                                <table className="min-w-full">
                                                    <thead className="bg-blue-gray-50">
                                                    <tr>
                                                        <th
                                                            scope="col"
                                                            className={"px-4 py-3 text-left text-xs font-medium " +
                                                                "text-blue-gray-500 uppercase font-mono tracking-wider"}
                                                        >
                                                            Version #
                                                        </th>
                                                        <th
                                                            scope="col"
                                                            className={"px-4 py-3 text-left text-xs font-medium " +
                                                                "text-blue-gray-500 uppercase font-mono tracking-wider"}

                                                        >
                                                            Report Date
                                                        </th>
                                                        <th
                                                            scope="col"
                                                            className={"px-4 py-3 text-left text-xs font-medium " +
                                                                "text-blue-gray-500 uppercase font-mono tracking-wider"}

                                                        >
                                                            File Type
                                                        </th>
                                                        <th
                                                            scope="col"
                                                            className={"px-4 py-3 text-left text-xs font-medium " +
                                                                "text-blue-gray-500 uppercase font-mono tracking-wider"}

                                                        >
                                                            Download
                                                        </th>
                                                        <th
                                                            scope="col"
                                                        >
                                                        </th>
                                                    </tr>
                                                    </thead>
                                                    <tbody className="bg-white">
                                                    {
                                                        report.documentVersions.length !== 0 ?
                                                            report.documentVersions.map((version) => (
                                                                <VersionRow
                                                                    key={report.identifier + version.versionNumber}
                                                                    documentIdentifier={report.identifier}
                                                                    version={version}
                                                                    handleDownloadVersion={handleDownloadVersion}
                                                                    handleDownloadSubsequentFile={handleDownloadSubsequentFile}
                                                                    showSubsequentFiles={showSubsequentFiles.includes(version.versionNumber)}
                                                                    handleShowSubsequentFiles={() => handleShowSubsequentFiles(version.versionNumber)}
                                                                    currentlyDownloading={currentlyDownloading}
                                                                />
                                                            ))
                                                            :
                                                            <tr>
                                                                <td colSpan={4}>
                                                                    <div className={"px-2 py-3 ml-2"}>
                                                                        Sorry, we could not find any versions of this
                                                                        document
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                    }
                                                    </tbody>
                                                </table>
                                            </div>
                                        </>
                                )
                            }

                            {detailOption === "Reupload" &&
                                <AccessControl requiredPermission={PERMISSIONS.UPLOAD_REPORTS}
                                               noAccessType={NO_ACCESS_TYPES.REDIRECT}>
                                    {(loading ?
                                            <SectionPlaceholder/>
                                            :
                                            <>
                                                <div className={"grid grid-flow-row gap-4 mb-8"}>
                                                    <Upload/>
                                                </div>
                                            </>
                                    )}
                                </AccessControl>
                            }

                            {detailOption === "Review/Release" &&
                                (loading ?
                                        <SectionPlaceholder/>
                                        :
                                        <div className="flex flex-col gap-4">
                                            <div className="flex items-center gap-16">
                                                <div className={"text-sm flex flex-col"}
                                                >
                                                    <span className="font-medium text-indigo-900">Release report</span>
                                                </div>
                                                <div className="flex space-between">
                                                    <Toggle name={"toggle-3"}/>
                                                </div>
                                            </div>
                                            <div className={"overflow-hidden border-b border-none shadow-sm " +
                                                "sm:rounded-lg"}
                                            >
                                                <table className="table-fixed min-w-full">
                                                    <thead className="bg-blue-gray-50">
                                                    <tr>
                                                        <th
                                                            scope="col"
                                                            className={"w-1/2 px-6 py-3 text-left text-xs " +
                                                                "font-medium text-blue-gray-500 uppercase font-mono " +
                                                                "tracking-wider"}
                                                        >
                                                            Staff
                                                        </th>
                                                        <th
                                                            scope="col"
                                                            className={"w-1/2 px-6 py-3 text-left text-xs " +
                                                                "font-medium text-blue-gray-500 uppercase font-mono " +
                                                                "tracking-wider"}>
                                                            Reviewing
                                                        </th>
                                                    </tr>
                                                    </thead>
                                                    <tbody className="bg-white">
                                                    <tr className>
                                                        <td className="px-6 py-4">
                                                            <div className="flex space-between">
                                                                <div>
                                                                    <div className={"text-sm font-medium " +
                                                                        "text-blue-gray-600"}
                                                                    >
                                                                        Gerald Geraldton
                                                                    </div>
                                                                    <div className="text-sm text-blue-gray-500 mt-1">
                                                                        gerald@geraldton.com
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div className="flex space-between">
                                                                <Toggle name={"toggle-1"}/>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td className="px-6 py-4">
                                                            <div className="flex space-between">
                                                                <div>
                                                                    <div className={"text-sm font-medium " +
                                                                        "text-blue-gray-600"}>
                                                                        Frank Franskton
                                                                    </div>
                                                                    <div className="text-sm text-blue-gray-500 mt-1">
                                                                        frank@frankston.com
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div className="flex space-between">
                                                                <Toggle name={"toggle-2"}/>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                )
                            }

                            {detailOption === "Tags" &&
                                <AccessControl
                                    requiredPermission={PERMISSIONS.EDIT_DOCUMENT_TAGS}
                                    noAccessType={NO_ACCESS_TYPES.REDIRECT}
                                >
                                    {(loading ?
                                            <SectionPlaceholder/>
                                            :
                                            <>
                                                <div className={"grid grid-flow-row gap-4 mb-8"}>
                                                    <Input
                                                        id="tags"
                                                        type="text"
                                                        name="Tags"
                                                        placeholder="Enter tag and press enter key"
                                                        value={tagInput}
                                                        onChange={setTagInput}
                                                        Icon={<TagIcon className="h-4 w-4 text-gray-500"/>}
                                                        onKeyPress={handleTagsKeyPress}
                                                    />
                                                    <div className="flex flex-wrap">
                                                        {reportTags && reportTags.map(tag =>
                                                            <Tag key={tag} text={tag}
                                                                 extraStyling="mr-2"
                                                                 onDelete={() => deleteTag(tag)}
                                                            />)}
                                                    </div>
                                                </div>
                                            </>
                                    )}
                                </AccessControl>
                            }

                        </div>

                        {detailOption === "Tags" &&
                            <div className={
                                "p-4 bg-gradient-to-l from-blue-50 to-teal-50 flex items-center justify-end " +
                                "text-right rounded-md"
                            }>
                                <div className="flex flex-row gap-3">
                                    <Button
                                        colour={BUTTON_VARIANT_TYPES.TEAL}
                                        text="Save changes"
                                        icon={<SaveIcon className="block h-4 w-4 mr-2"/>}
                                        iconPositionLeft
                                        onClick={handleOnClickSave}
                                        loading={submitLoading}
                                        disabled={loading || !changesMade}
                                    />
                                </div>
                            </div>
                        }

                    </div>
                </div>
            </div>
        </DashboardContainer>
    )
}

export default ManageReport;
